interface Coordinates {
  latitude: number;
  longitude: number;
}

interface CachedLocation extends Coordinates {
  timestamp: number;
  source: 'geolocation' | 'ip' | 'default';
}

const CACHE_KEY = 'userLocationCache';
const CACHE_DURATION = 1000 * 60 * 30; // 30 minutes
const PERMISSION_CACHE_KEY = 'locationPermissionStatus';
const PERMISSION_TIMESTAMP_KEY = 'locationPermissionTimestamp';

function getCachedLocation(): CachedLocation | null {
  const cached = localStorage.getItem(CACHE_KEY);
  if (!cached) return null;

  const location: CachedLocation = JSON.parse(cached);
  const age = Date.now() - location.timestamp;

  // Return null if cache is expired
  return age < CACHE_DURATION ? location : null;
}

function cacheLocation(coordinates: Coordinates, source: CachedLocation['source']) {
  const location: CachedLocation = {
    ...coordinates,
    timestamp: Date.now(),
    source
  };
  localStorage.setItem(CACHE_KEY, JSON.stringify(location));
  return location;
}

// Store permission status in localStorage
function storeLocationPermissionStatus(status: 'granted' | 'denied' | 'prompt') {
  localStorage.setItem(PERMISSION_CACHE_KEY, status);
  localStorage.setItem(PERMISSION_TIMESTAMP_KEY, Date.now().toString());
}

// Get stored permission status
function getStoredLocationPermissionStatus(): string | null {
  return localStorage.getItem(PERMISSION_CACHE_KEY);
}

// Check if we've already requested permission recently
function hasRecentlyRequestedPermission(): boolean {
  const timestamp = localStorage.getItem(PERMISSION_TIMESTAMP_KEY);
  if (!timestamp) return false;
  
  // Consider permission requests within the last 24 hours as "recent"
  const oneDayInMs = 24 * 60 * 60 * 1000;
  return (Date.now() - parseInt(timestamp)) < oneDayInMs;
}

export async function getCurrentLocation(defaultLat: number = 39.8283, defaultLng: number = -98.5795): Promise<CachedLocation> {
  // Check cache first
  const cached = getCachedLocation();
  if (cached) {
    return cached;
  }

  try {
    const position = await getUserLocation();
    // Store permission status as granted when location is successfully retrieved
    storeLocationPermissionStatus('granted');
    return cacheLocation({
      latitude: position.coords.latitude,
      longitude: position.coords.longitude
    }, 'geolocation');
  } catch (error) {
    console.warn('Geolocation failed:', error);
    
    // If permission denied, store that status
    if (error instanceof GeolocationPositionError && error.code === error.PERMISSION_DENIED) {
      storeLocationPermissionStatus('denied');
    }

    try {
      const response = await fetch('https://ipapi.co/json/')
      const data = await response.json()
      return cacheLocation({
        latitude: parseFloat(data.latitude),
        longitude: parseFloat(data.longitude)
      }, 'ip');
    } catch (error) {
      console.warn('IP geolocation failed:', error);
      return cacheLocation({
        latitude: defaultLat,
        longitude: defaultLng
      }, 'default');
    }
  }
}

export async function getUserLocation(): Promise<GeolocationPosition> {
  return new Promise(async (resolve, reject) => {
    if (!navigator.geolocation) {
      await fallbackToGoogleGeolocation(resolve, reject);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      resolve,
      async (error) => {
        // If user denies location access, try Google Geolocation
        await fallbackToGoogleGeolocation(resolve, reject);
      },
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 60000 // Cache location for 1 minute
      }
    );
  });
}

async function fallbackToGoogleGeolocation(
  resolve: (value: GeolocationPosition) => void,
  reject: (reason?: any) => void
) {
  try {
    const apiKey = document.querySelector<HTMLScriptElement>('script[src*="maps.googleapis.com"]')?.src.split('key=')[1]?.split('&')[0];
    if (!apiKey) {
      throw new Error('Google Maps API key not found');
    }

    // Get client's IP-based location using Google Maps Geolocation API
    const response = await fetch(`https://www.googleapis.com/geolocation/v1/geolocate?key=${apiKey}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        considerIp: true,
      }),
    });
    
    if (!response.ok) {
      throw new Error('Google Geolocation API request failed');
    }

    const data = await response.json();
    
    resolve({
      coords: {
        latitude: data.location.lat,
        longitude: data.location.lng,
        accuracy: data.accuracy || 0,
        altitude: null,
        altitudeAccuracy: null,
        heading: null,
        speed: null,
        toJSON: function() {
          return {
            latitude: this.latitude,
            longitude: this.longitude,
            accuracy: this.accuracy,
            altitude: this.altitude,
            altitudeAccuracy: this.altitudeAccuracy,
            heading: this.heading,
            speed: this.speed
          };
        }
      },
      timestamp: Date.now(),
      toJSON: function() {
        return {
          coords: this.coords,
          timestamp: this.timestamp
        };
      }
    });
  } catch (error) {
    reject(error);
  }
}

export function shouldShowLocationRequest(): boolean {
  const cached = getCachedLocation();
  const storedStatus = getStoredLocationPermissionStatus();
  
  // Don't show if we have a recent cached location from geolocation
  if (cached?.source === 'geolocation') {
    return false;
  }

  // If we have a stored status of 'granted', no need to ask again
  if (storedStatus === 'granted') {
    return false;
  }
  
  // If user previously denied and we asked recently, don't ask again right away
  if (storedStatus === 'denied' && hasRecentlyRequestedPermission()) {
    return false;
  }

  // No custom confirmation dialog - let the browser handle permissions
  return true;
}

export function clearLocationCache(): void {
  localStorage.removeItem(CACHE_KEY);
  localStorage.removeItem(PERMISSION_CACHE_KEY);
  localStorage.removeItem(PERMISSION_TIMESTAMP_KEY);
}